import { useEffect, useState } from "react";
import {
  UICircularProgress,
  UIIconButton,
  UIListItemIcon,
  UIListItemText,
  UIMenu,
  UIMenuItem,
  UITooltip
} from "../../_core/components";
import {
  Email as EmailIcon,
  MoreVert as MoreVertIcon,
  UnsubscribeOutlined as UnsubscribeIcon
} from "../../_core/components/icons";
import { useApiAlerts } from "../../_core/hooks";
import RemoveEmailSuppressionDialog from "../../_core/components/ui/RemoveEmailSuppressionDialog";
import { isEmailSuppressed } from "../../_core/utils/emailDelivery";
import { PartnerUser } from "../api/types";
import { useRemovePartnerUserEmailSuppression, useResendPartnerUserWelcomeEmail } from "../hooks";

export interface PartnerUserActionsDropdownProps {
  user: PartnerUser;
}

const PartnerUserActionsDropdown = ({ user }: PartnerUserActionsDropdownProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isSuppressionDialogOpen, setIsSuppressionDialogOpen] = useState(false);
  const open = Boolean(anchorEl);

  const {
    mutate: executeResendEmail,
    isSuccess: isEmailSuccess,
    error: emailError,
    isError: isEmailError,
    isPending: isEmailLoading,
  } = useResendPartnerUserWelcomeEmail();

  const {
    mutate: executeRemoveSuppression,
    isSuccess: isSuppressionSuccess,
    error: suppressionError,
    isError: isSuppressionError,
    isPending: isSuppressionLoading,
  } = useRemovePartnerUserEmailSuppression();

  const getSuccessMessage = () => {
    if (isEmailSuccess) return "Welcome email sent";
    if (isSuppressionSuccess) return "Email address removed from the suppression list";

    return undefined;
  };

  useApiAlerts(
    getSuccessMessage(),
    emailError || suppressionError,
    isEmailError || isSuppressionError
  );

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleResendWelcomeEmail = () => {
    executeResendEmail(user.id);
  };

  const handleRemoveSuppression = () => {
    setIsSuppressionDialogOpen(true);
    handleClose();
  };

  const handleCloseSuppressionDialog = () => {
    if (!isSuppressionLoading) setIsSuppressionDialogOpen(false);
  };

  const handleConfirmRemoveSuppression = () => {
    executeRemoveSuppression(user.id);
  };

  useEffect(() => {
    if (isSuppressionSuccess || isSuppressionError) {
      setIsSuppressionDialogOpen(false);
    }
  }, [isSuppressionSuccess, isSuppressionError]);

  useEffect(() => {
    if (isEmailSuccess || isEmailError) {
      handleClose();
    }
  }, [isEmailSuccess, isEmailError]);

  const isSuppressed = isEmailSuppressed(user.emailDeliveryFailure);
  const hasResendEmail = !user.confirmed;
  const hasRemoveSuppression = isSuppressed;

  if (!hasResendEmail && !hasRemoveSuppression) {
    return null;
  }

  const resendMenuItem = (
    <UIMenuItem
      onClick={handleResendWelcomeEmail}
      disabled={isEmailLoading || isSuppressed}
      dense
    >
      <UIListItemIcon>
        {isEmailLoading ? (
          <UICircularProgress size={16} />
        ) : (
          <EmailIcon fontSize="small" />
        )}
      </UIListItemIcon>
      <UIListItemText primary="Resend Welcome Email" />
    </UIMenuItem>
  );

  return (
    <>
      <UITooltip title="Actions">
        <UIIconButton
          onClick={handleClick}
          size="small"
          aria-controls={open ? `partner-user-actions-menu-${user.id}` : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
        >
          <MoreVertIcon fontSize="small" />
        </UIIconButton>
      </UITooltip>

      <UIMenu
        id={`partner-user-actions-menu-${user.id}`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ dense: true }}
      >
        {hasResendEmail && isSuppressed && (
          <UITooltip title="Remove the address from the suppression list first" placement="left">
            <span>{resendMenuItem}</span>
          </UITooltip>
        )}
        {hasResendEmail && !isSuppressed && resendMenuItem}

        {hasRemoveSuppression && (
          <UIMenuItem onClick={handleRemoveSuppression} dense>
            <UIListItemIcon>
              <UnsubscribeIcon fontSize="small" />
            </UIListItemIcon>
            <UIListItemText primary="Remove from Suppression List" />
          </UIMenuItem>
        )}
      </UIMenu>

      <RemoveEmailSuppressionDialog
        open={isSuppressionDialogOpen}
        failure={user.emailDeliveryFailure}
        userName={user.name}
        isLoading={isSuppressionLoading}
        onConfirm={handleConfirmRemoveSuppression}
        onCancel={handleCloseSuppressionDialog}
      />
    </>
  );
};

export default PartnerUserActionsDropdown;
